/**
 * Shipping protection upsell on physical product pages.
 *
 * The card is a checkbox: ticking it adds the protection product to the cart
 * straight away (quantity 1), unticking removes it. The same request renders
 * the drawer, the cart bubble and the header entries chip, and the checkbox
 * follows the cart whenever anything else changes it.
 */
(() => {
  if (window.FFShippingProtection) return;

  const ENFORCE_HEADER = 'X-FF-Multiplier-Enforce';

  function getRenderTargets() {
    const targets = [];
    const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (drawerInner) targets.push({ section: 'cart-drawer', selector: '.drawer__inner', el: drawerInner });
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ section: 'cart-icon-bubble', selector: '.shopify-section', el: bubble });
    const chip = document.querySelector('ff-header-entries');
    if (chip) targets.push({ section: 'ff-header-entries', selector: 'ff-header-entries', el: chip, chip: true });
    return targets;
  }

  function applySections(sections, targets) {
    if (!sections) return;
    for (const target of targets) {
      const html = sections[target.section];
      if (!html) continue;
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (!found) continue;
      if (target.chip) target.el.dataset.total = found.dataset.total || '';
      target.el.innerHTML = found.innerHTML;
      if (target.section === 'cart-drawer') {
        const rendered = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        const drawer = document.querySelector('cart-drawer');
        if (rendered && drawer) {
          const empty = rendered.classList.contains('is-empty');
          drawer.classList.toggle('is-empty', empty);
          target.el.classList.toggle('is-empty', empty);
        }
      }
    }
  }

  async function postCart(url, body) {
    const targets = getRenderTargets();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [ENFORCE_HEADER]: '1' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ ...body, sections: targets.map((t) => t.section), sections_url: window.location.pathname }),
    });
    if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
    const data = await res.json();
    applySections(data.sections, targets);
    document.dispatchEvent(new CustomEvent('ff:entries:updated', { detail: { cart: data } }));
    return data;
  }

  class FFShippingProtection extends HTMLElement {
    #busy = false;
    #unsubscribe = null;

    connectedCallback() {
      this.input = this.querySelector('input[type="checkbox"]');
      if (!this.input) return;
      this.input.addEventListener('change', () => this.#onChange());
      // Other scripts change the cart too (drawer remove, boosters); follow them.
      document.addEventListener('ff:entries:updated', (event) => this.#syncFrom(event.detail?.cart));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.#unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.#syncFrom(event?.cartData));
      }
    }

    disconnectedCallback() {
      this.#unsubscribe?.();
    }

    get variantId() {
      return Number(this.dataset.variantId) || 0;
    }

    #setBusy(busy) {
      this.#busy = busy;
      this.toggleAttribute('aria-busy', busy);
      this.input.disabled = busy;
    }

    #setError(message) {
      const node = this.querySelector('[data-ff-protect-error]');
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
    }

    async #syncFrom(cart) {
      let state = cart;
      if (!state || !Array.isArray(state.items)) {
        try {
          const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
          if (!res.ok) return;
          state = await res.json();
        } catch (err) {
          return;
        }
      }
      if (this.#busy) return;
      this.input.checked = state.items.some((item) => Number(item.variant_id) === this.variantId);
    }

    async #onChange() {
      if (this.#busy || !this.variantId) return;
      const wanted = this.input.checked;
      this.#setError('');
      this.#setBusy(true);
      try {
        if (wanted) {
          await postCart('/cart/add.js', { items: [{ id: this.variantId, quantity: 1 }] });
        } else {
          await postCart('/cart/update.js', { updates: { [this.variantId]: 0 } });
        }
      } catch (err) {
        console.error('[ff-protect]', err);
        this.input.checked = !wanted;
        this.#setError(this.dataset.errorText || 'Could not update your cart. Please try again.');
      } finally {
        this.#setBusy(false);
      }
    }
  }

  window.FFShippingProtection = FFShippingProtection;
  customElements.define('ff-shipping-protection', FFShippingProtection);
})();
